// Proposal PDF generator. Same jsPDF conventions as src/App.js's
// generateGenericPDF (unit "pt", format "letter", 60pt margins).
//
// It renders exactly the `proposal` it is handed: { lines: [{ description,
// quantity, amount }], total }, built by jobMath.buildProposal from
// toProposalLines. Markup is never its own line; it is spread across the
// line amounts before this function sees them (spreadMarkup), so the
// printed lines always sum exactly to the printed total. A line without a
// finite amount throws rather than printing $0.00.
//
// "Powered by ReportForge" never appears here (spec section 3), unlike
// every other in-app screen. Missing brand contact items and job fields are
// omitted, never faked. An empty brand.terms prints a visible "Terms not
// set" notice. No em-dash appears anywhere in this file's printed copy
// (house rule for anything a customer reads); tools/verify-proposal-pdf.mjs
// checks the extracted text for one.
import { jsPDF } from "jspdf";

const money = (n) => n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const text = (v) => (v === undefined || v === null ? "" : String(v).trim());

// Fix round 1 (R23): printed near the title and in every page footer of a
// draft preview, so a preview can't pass for the proposal that was sent.
export const DRAFT_PREVIEW_MARK = "DRAFT PREVIEW. NOT A SENT PROPOSAL.";

// Fix round 1 (R24): until the client layer supplies a real brand, a
// proposal carries no company name at all, never the product's name, and
// no contact or terms (so "Terms not set" prints). Everything else in the
// house brand is kept as is.
export function interimProposalBrand(houseBrand) {
  return { ...houseBrand, displayName: "", contact: {}, terms: "" };
}

// baseId is the first job in the revisionOf chain (jobMath.proposalBaseId),
// so every revision of one bid shares a number: 11111111-R1, 11111111-R2.
export function proposalNumber(job, baseId) {
  return `${String(baseId || job.id).slice(0, 8)}-R${job.revision || 1}`;
}

export function proposalFileName(job, { preview = false } = {}) {
  const base = (text(job.name) || "job").replace(/[\\/:*?"<>|]+/g, "").replace(/\s+/g, "_");
  return `Proposal_${base}_R${job.revision || 1}${preview ? "_DRAFT" : ""}.pdf`;
}

// options: { preview } marks the PDF as a draft preview (R23); { baseId }
// sets the proposal number's base (proposalNumber). { saveAs(doc, name) }
// replaces doc.save for tests only (jsPDF puts save on each instance, so it
// can't be stubbed on the prototype). With returnBlob false, the file name
// used is returned.
export function generateProposalPdf(job, proposal, brand, returnBlob = false, { preview = false, baseId = null, saveAs = (d, name) => d.save(name) } = {}) {
  const lines = proposal?.lines || [];
  for (const pl of lines) {
    if (!Number.isFinite(pl.amount)) throw new Error(`Proposal line "${text(pl.description) || "Line item"}" has no amount.`);
  }
  if (!Number.isFinite(proposal?.total)) throw new Error("The proposal has no total.");

  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 60;
  const contentW = pageW - margin * 2;
  const bottom = pageH - 80; // leaves room for the page footer
  const qtyX = margin + contentW - 160;
  const descW = qtyX - (margin + 8) - 16;
  let y = 60;

  // ── Letterhead ─────────────────────────────────────────────────────────
  // Only an embedded (data URL) logo is drawn; anything jsPDF can't embed
  // is omitted, never replaced by a stand-in.
  if (typeof brand?.logoUrl === "string" && brand.logoUrl.startsWith("data:image")) {
    try { doc.addImage(brand.logoUrl, pageW - margin - 80, y - 10, 80, 40); } catch { /* unsupported format: omit */ }
  }
  const displayName = text(brand?.displayName);
  if (displayName) {
    doc.setFontSize(20); doc.setFont("helvetica", "bold"); doc.setTextColor(17, 24, 39);
    doc.text(displayName, margin, y + 20);
  }
  y += 40;
  const contact = brand?.contact || {};
  const contactLines = [
    text(contact.address),
    [text(contact.phone), text(contact.email)].filter(Boolean).join("   "),
    text(contact.license) ? `License #${text(contact.license)}` : "",
  ].filter(Boolean);
  doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(107, 114, 128);
  contactLines.forEach((line) => { doc.text(line, margin, y); y += 12; });
  y += 8; doc.setDrawColor(229, 231, 235); doc.setLineWidth(0.75); doc.line(margin, y, pageW - margin, y); y += 28;

  // ── Body header ────────────────────────────────────────────────────────
  doc.setFontSize(16); doc.setFont("helvetica", "bold"); doc.setTextColor(17, 24, 39);
  doc.text("Proposal", margin, y);
  const dateText = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(107, 114, 128);
  doc.text(`Proposal #${proposalNumber(job, baseId)}`, pageW - margin, y - 8, { align: "right" });
  doc.text(dateText, pageW - margin, y + 4, { align: "right" });
  if (preview) {
    doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(220, 38, 38);
    doc.text(DRAFT_PREVIEW_MARK, margin, y + 16);
    y += 16;
  }
  y += 24;
  const bodyLines = [text(job.contact), text(job.property), text(job.address)].filter(Boolean);
  doc.setFontSize(11); doc.setFont("helvetica", "normal"); doc.setTextColor(31, 41, 55);
  bodyLines.forEach((line) => { doc.text(line, margin, y); y += 15; });
  y += 14;

  // ── Scope table. Markup is already spread across the lines. ────────────
  const tableHeader = () => {
    doc.setFillColor(243, 244, 246); doc.rect(margin, y - 13, contentW, 22, "F");
    doc.setFontSize(9); doc.setFont("helvetica", "bold"); doc.setTextColor(107, 114, 128);
    doc.text("DESCRIPTION", margin + 8, y + 2);
    doc.text("QTY", qtyX, y + 2);
    doc.text("AMOUNT", margin + contentW - 8, y + 2, { align: "right" });
    y += 26;
  };
  tableHeader();
  for (const pl of lines) {
    doc.setFontSize(11); doc.setFont("helvetica", "normal");
    const descLines = doc.splitTextToSize(text(pl.description) || "Line item", descW);
    const rowH = descLines.length * 14 + 4;
    if (y + rowH > bottom) { doc.addPage(); y = 72; tableHeader(); }
    doc.setFontSize(11); doc.setFont("helvetica", "normal"); doc.setTextColor(31, 41, 55);
    descLines.forEach((dl, i) => doc.text(dl, margin + 8, y + i * 14));
    doc.text(String(pl.quantity ?? 1), qtyX, y);
    doc.text(`$${money(pl.amount)}`, margin + contentW - 8, y, { align: "right" });
    y += rowH;
  }
  if (y + 40 > bottom) { doc.addPage(); y = 72; }
  y += 2; doc.setDrawColor(229, 231, 235); doc.setLineWidth(0.75); doc.line(margin, y, pageW - margin, y); y += 22;
  doc.setFontSize(13); doc.setFont("helvetica", "bold"); doc.setTextColor(17, 24, 39);
  doc.text("Total", qtyX, y);
  doc.text(`$${money(proposal.total)}`, margin + contentW - 8, y, { align: "right" });
  y += 36;

  // ── Terms ──────────────────────────────────────────────────────────────
  const terms = text(brand?.terms);
  const termsLines = doc.splitTextToSize(
    terms || "Terms not set. No inclusions, exclusions or validity terms are on file for this proposal.",
    contentW,
  );
  if (y + 14 + 13 > bottom) { doc.addPage(); y = 72; }
  doc.setFontSize(9); doc.setFont("helvetica", "bold"); doc.setTextColor(107, 114, 128);
  doc.text("TERMS", margin, y); y += 14;
  doc.setFont("helvetica", "normal");
  if (terms) doc.setTextColor(31, 41, 55); else doc.setTextColor(220, 38, 38);
  termsLines.forEach((line) => {
    if (y > bottom) { doc.addPage(); y = 72; doc.setFontSize(9); doc.setFont("helvetica", "normal"); if (terms) doc.setTextColor(31, 41, 55); else doc.setTextColor(220, 38, 38); }
    doc.text(line, margin, y); y += 13;
  });

  // ── Page footer: page numbers and the proposal number only. ────────────
  const pages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p); doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.setTextColor(156, 163, 175);
    doc.setDrawColor(229, 231, 235); doc.setLineWidth(0.5); doc.line(margin, pageH - 22 - 14, pageW - margin, pageH - 22 - 14);
    doc.text(`Proposal #${proposalNumber(job, baseId)}`, margin, pageH - 22);
    if (preview) {
      doc.setFont("helvetica", "bold"); doc.setTextColor(220, 38, 38);
      doc.text(DRAFT_PREVIEW_MARK, pageW / 2, pageH - 22, { align: "center" });
      doc.setFont("helvetica", "normal"); doc.setTextColor(156, 163, 175);
    }
    doc.text(`Page ${p} of ${pages}`, pageW - margin, pageH - 22, { align: "right" });
  }

  if (returnBlob) return doc.output("blob");
  // Fix round 2 (R23): a preview downloads as ..._DRAFT.pdf.
  const fileName = proposalFileName(job, { preview });
  saveAs(doc, fileName);
  return fileName;
}
